import * as React from 'react';
import {DragDropContext, Droppable} from 'react-beautiful-dnd';
import type {DropResult} from 'react-beautiful-dnd';

import {DraggableListItem} from './draggable-list-item';

export type ListItem = {
    id: string;
    component: React.ReactNode;
    isActive?: boolean;
    isDisabled?: boolean;
};

type Props = {
    listItems: ListItem[];
    handleReorder: (startIndex: number, endIndex: number) => void;
    showDraggableRowOnlyOnHover?: boolean;
    isInModal?: boolean;
    disabled?: boolean;
    showOverflow?: boolean;
    showHandleWhenDisabled?: boolean;
};

export const DraggableList = (props: Props) => {
    const [draggedItemId, setDraggedItemId] = React.useState<string | null | undefined>(null);
    const [hoveredItemId, setHoveredItemId] = React.useState<string | null | undefined>(null);

    const getDraggingStyle = (draggableStyle: any) => {
        const {left, top, ...restStyles} = draggableStyle;

        // this avoids setting the top to NaN when dragging, which causes the draggable item to jump to the bottom of the page
        let newTop;
        if (!isNaN(top)) {
            newTop = top;
        }

        const draggingStyle = {
            margin: '0 0 8px 0',
            left: props.isInModal ? undefined : left, // solves a bug where the draggable item would jump to the right when in a modal
            top: props.isInModal && newTop ? newTop - 50 : top, // solves a bug where the draggable item would jump down when in a modal
            // styles we need to apply on draggables
            ...restStyles,
        } as const;

        // This locks the x-axis so the item is only draggable vertically
        if (draggableStyle.transform) {
            const axisLockY = `translate(0px${draggableStyle.transform.slice(
                draggableStyle.transform.indexOf(','),
                draggableStyle.transform.length
            )}`;

            return {
                ...draggingStyle,
                transform: axisLockY,
            };
        }

        // Fallback in case there is no transform in the style object
        return draggingStyle;
    };

    const handleDragEnd = (result: DropResult) => {
        setDraggedItemId(null);
        if (!result.destination || props.disabled) return;
        props.handleReorder(result.source.index, result.destination.index);
    };

    return (
        <DragDropContext
            onDragEnd={handleDragEnd}
            onDragStart={(e) => {
                if (props.disabled) return;
                setDraggedItemId(e.draggableId);
            }}
        >
            <Droppable droppableId='droppable'>
                {(droppableProvided) => (
                    <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                        {props.listItems.map((item, index) => {
                            return (
                                <DraggableListItem
                                    key={item.id}
                                    index={index}
                                    item={item}
                                    draggedItemId={draggedItemId}
                                    setDraggedItemId={setDraggedItemId}
                                    hoveredItemId={hoveredItemId}
                                    setHoveredItemId={setHoveredItemId}
                                    getDraggingStyle={getDraggingStyle}
                                    showOverflow={props.showOverflow}
                                    showHandleWhenDisabled={props.showHandleWhenDisabled}
                                    disabled={props.disabled}
                                    showDraggableRowOnlyOnHover={props.showDraggableRowOnlyOnHover}
                                />
                            );
                        })}
                        {droppableProvided.placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
};
